import clsx from "clsx";
import type { Ref } from "react";

/**
 * Material Symbols (https://fonts.google.com/icons) の SVG を `apps/webapp/public/i/dx/` に
 * 配置し、 CSS の `mask-image` + `background-color: currentColor` で着色する。 これにより
 * `text-dx-*` のような Tailwind 文字色クラスで色テーマを継承できる。
 *
 * SVG 自体は viewBox `0 -960 960 960` の Material Symbols 仕様で、 `fill` は使わない
 * (mask は alpha のみを利用する)。 サイズは 20×20 をデフォルトとする。
 *
 * 旧 `content-icons.tsx` と `room-detail/_components/_icons.tsx` が個別に同等実装を
 * 持っていたものを、 本ファイルに統合した。
 */
export function DxIcon({
  src,
  className,
  size,
  ref,
}: {
  src: string;
  className?: string;
  ref?: Ref<HTMLSpanElement>;
  /** px 指定。 渡すと既定 size-[20px] や className の size 指定より優先される
   *  (mask アイコンは size-[] の二重指定が CSS ソース順で競合するため、 確実に効かせたい時に使う)。 */
  size?: number;
}) {
  return (
    <span
      ref={ref}
      aria-hidden
      className={clsx("block size-[20px] shrink-0 bg-current", className)}
      style={{
        ...(size !== undefined ? { width: size, height: size } : {}),
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}

type IconProps = { className?: string; size?: number };

export function PersonIcon({ className, size }: IconProps) {
  return <DxIcon src="/i/dx/person.svg" className={className} size={size} />;
}

/** 旧 SANU アイコンセットの person。 PersonIcon (Material Symbols) とは見た目が異なる */
export function PersonOutlineIcon({ className }: IconProps) {
  return <DxIcon src="/i/icon_person.svg" className={className} />;
}

export function RouteIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/route.svg" className={className} />;
}

export function SizeIcon({ className, size }: IconProps) {
  return <DxIcon src="/i/dx/crop.svg" className={className} size={size} />;
}

/** SizeIcon と同じ `/i/dx/crop.svg` を指す。 room-detail からの呼び出し互換用 */
export const CropIcon = SizeIcon;

export function DirectionsCarIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/directions-car.svg" className={className} />;
}

export function TrainIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/train.svg" className={className} />;
}

export function DirectionsBusIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/directions-bus.svg" className={className} />;
}

export function FlightIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/flight.svg" className={className} />;
}

export function DirectionsBoatIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/directions-boat.svg" className={className} />;
}

export function DirectionsWalkIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/directions-walk.svg" className={className} />;
}

export function DirectionsBikeIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/directions-bike.svg" className={className} />;
}

export function MonitorIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/monitor.svg" className={className} />;
}

export function ElectricalServicesIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/electrical-services.svg" className={className} />;
}

export function DogIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/dog.svg" className={className} />;
}

export function BathtubIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/bathtub.svg" className={className} />;
}

export function InfoIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/info.svg" className={className} />;
}

export function WarnIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/warn.svg" className={className} />;
}

/** 丸に「!」(Material Symbols error_outline)。 注意喚起 Info の見出しアイコン。 */
export function ErrorIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/error.svg" className={className} />;
}

export function PhoneIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/phone.svg" className={className} />;
}

export function MapIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/map.svg" className={className} />;
}

export function LanguageIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/language.svg" className={className} />;
}

// 外部サイトリンク用。 見た目は language と同じ glyph だが、 言語セレクタの
// LanguageIcon と用途が違うため別アセットで分離 (片方のデザイン変更が他方に波及しない)。
export function ExternalSiteIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/external-site.svg" className={className} />;
}

export function OpenInNewIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/open-in-new.svg" className={className} />;
}

export function ChevronRightIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/chevron-right.svg" className={className} />;
}

export function ArrowBackIcon({ className, size }: IconProps) {
  return (
    <DxIcon src="/i/dx/arrow-back.svg" className={className} size={size} />
  );
}

export function ExpandMoreIcon({ className, size }: IconProps) {
  return (
    <DxIcon src="/i/dx/expand-more.svg" className={className} size={size} />
  );
}

export function CloseIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/close.svg" className={className} />;
}

export function CalendarIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/calendar.svg" className={className} />;
}

export function RestaurantMenuIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/restaurant-menu.svg" className={className} />;
}

export function DryCleaningIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/dry-cleaning.svg" className={className} />;
}

export function ShoppingCartIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/shopping-cart.svg" className={className} />;
}

export function BarbecueIcon({ className }: IconProps) {
  return <DxIcon src="/i/dx/outdoor-grill.svg" className={className} />;
}
